import { APIClient } from 'infra/APIClient'
import {
  DraftPayoutResource,
  ListPendingTransactionsRequest,
  PendingTransactionResource,
  ApproveTransactionsRequest,
  ApproveTransactionsResponse,
  RemoveTransactionsRequest,
  RemoveTransactionsResponse,
  SubmitDraftPayoutRequest,
  SubmitDraftPayoutResponse,
} from './dtos/drafting'
import { AipCursorPaged, APIConfig } from 'types/definitions'
import {
  AdminTransactionResource,
  CreateUserTransactionRequest,
  CreateUserTransactionResponse,
  CancelUserTransactionResponse,
  ListAdminHistoricalTransactionsRequest,
  AdminHistoricalTransactionResource,
  DownloadAdminHistoricalTransactionsAsCsvRequest,
} from './dtos/admin-transactions'
import { rethrowRequestError } from 'utils/error-util'
import { PayoutResource } from './dtos/payouts'
import { getHttpErrorConstructorForStatusCode } from 'fejl'
import { CancellationToken } from '../../infra/cancellation'

/**
 * APIs for working with Payouts in the BigMoney service.
 */
export interface BigMoneyPayoutsAdminAPI {
  draftPayouts: DraftPayoutAPI
  pendingTransactions: PendingTransactionAPI
  transactions: AdminTransactionAPI
  payouts: AdminPayoutAPI
}

/**
 * Creates a new BigMoney Payouts API suite of clients.
 * @param opts
 */
export function createBigMoneyPayoutsAdminAPI(
  opts: APIConfig,
): BigMoneyPayoutsAdminAPI {
  return {
    draftPayouts: new DraftPayoutAPI(opts),
    pendingTransactions: new PendingTransactionAPI(opts),
    transactions: new AdminTransactionAPI(opts),
    payouts: new AdminPayoutAPI(opts),
  }
}

/**
 * API for working with draft payouts.
 */
export class DraftPayoutAPI extends APIClient {
  get(id: string): Promise<DraftPayoutResource> {
    return this.client
      .get(`/payouts/admin/v1/draft-payouts/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  getCurrent(): Promise<DraftPayoutResource> {
    return this.client
      .get(`/payouts/admin/v1/draft-payouts/current`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  approveTransactions(
    id: string,
    request: ApproveTransactionsRequest,
  ): Promise<ApproveTransactionsResponse> {
    return this.client
      .post(
        `/payouts/admin/v1/draft-payouts/${id}/approve-transactions`,
        request,
      )
      .then(this._data)
      .catch(rethrowRequestError)
  }

  removeTransactions(
    id: string,
    request: RemoveTransactionsRequest,
  ): Promise<RemoveTransactionsResponse> {
    return this.client
      .post(
        `/payouts/admin/v1/draft-payouts/${id}/remove-transactions`,
        request,
      )
      .then(this._data)
      .catch(rethrowRequestError)
  }

  submitPayout(
    id: string,
    request: SubmitDraftPayoutRequest,
  ): Promise<SubmitDraftPayoutResponse> {
    return this.client
      .post(`/payouts/admin/v1/draft-payouts/${id}/submit`, request)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API for working with payouts (admin perspective).
 */
export class AdminPayoutAPI extends APIClient {
  get(id: string): Promise<PayoutResource> {
    return this.client
      .get(`/payouts/admin/v1/payouts/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API for working with draft payouts.
 */
export class PendingTransactionAPI extends APIClient {
  list(
    request: ListPendingTransactionsRequest,
    cancellationToken = CancellationToken.default,
  ): Promise<AipCursorPaged<PendingTransactionResource>> {
    return this.client
      .get(`/payouts/admin/v1/pending-transactions`, {
        signal: cancellationToken.asAbortSignal(),
        params: {
          page_size: request.page_size,
          page_token: request.page_token,
          order_by: request.order_by,
          search_text: request.search_text,
          flags: request.flags,
          transaction_source: request.transaction_source,
          date_created_from: request.date_created_from?.toISOString(),
          date_created_to: request.date_created_to?.toISOString(),
          date_transacted_from: request.date_transacted_from?.toISOString(),
          date_transacted_to: request.date_transacted_to?.toISOString(),
          workspace_ids: request.workspace_ids,
          recipient_user_id: request.recipient_user_id,
        },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }
}

/**
 * API for working with transactions (admin perspective).
 */
export class AdminTransactionAPI extends APIClient {
  listHistoricalTransactions(
    request: ListAdminHistoricalTransactionsRequest,
    cancellationToken = CancellationToken.default,
  ): Promise<AipCursorPaged<AdminHistoricalTransactionResource>> {
    return this.client
      .get('/payouts/admin/v1/transactions', {
        signal: cancellationToken.asAbortSignal(),
        params: {
          page_size: request.page_size,
          page_token: request.page_token,
          order_by: request.order_by,
          search_text: request.search_text,
          flags: request.flags,
          transaction_source: request.transaction_source,
          date_completed_from: request.date_completed_from?.toISOString(),
          date_completed_to: request.date_completed_to?.toISOString(),
          workspace_ids: request.workspace_ids,
          recipient_user_id: request.recipient_user_id,
        },
      })
      .then(this._data)
      .catch(rethrowRequestError)
  }

  async downloadHistoricalTransactionsAsCsv(
    request: DownloadAdminHistoricalTransactionsAsCsvRequest,
  ): Promise<ReadableStream<Uint8Array>> {
    // Need to use `fetch` to get a stream.
    const params = new URLSearchParams({
      ...(request.search_text && { search_text: request.search_text }),
      ...(request.workspace_ids && { workspace_ids: request.workspace_ids }),
      ...(request.flags && { flags: request.flags }),
      ...(request.transaction_source && {
        transaction_source: request.transaction_source,
      }),
      ...(request.recipient_user_id && {
        recipient_user_id: request.recipient_user_id,
      }),
      ...(request.date_completed_from && {
        date_completed_from: request.date_completed_from.toISOString(),
      }),
      ...(request.date_completed_to && {
        date_completed_to: request.date_completed_to.toISOString(),
      }),
    })
    const response = await fetch(
      `${this.config.baseURL}/payouts/admin/v1/transactions.csv?${params}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${this.token}`,
        },
      },
    )

    if (response.status !== 200) {
      const message = await response.json().then((m) => m.message)
      const ErrorClass = getHttpErrorConstructorForStatusCode(response.status)
      throw new ErrorClass(message)
    }

    return response.body!
  }

  get(id: string): Promise<AdminTransactionResource> {
    return this.client
      .get(`/payouts/admin/v1/transactions/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  createUserTransaction(
    request: CreateUserTransactionRequest,
  ): Promise<CreateUserTransactionResponse> {
    return this.client
      .post(`/payouts/admin/v1/transactions`, request)
      .then(this._data)
      .catch(rethrowRequestError)
  }

  cancelUserTransaction(id: string): Promise<CancelUserTransactionResponse> {
    return this.client
      .delete(`/payouts/admin/v1/transactions/${id}`)
      .then(this._data)
      .catch(rethrowRequestError)
  }
}
